import {
    keepPreviousData,
    useMutation,
    useQuery,
    useQueryClient,
    type QueryClient,
} from '@tanstack/react-query';

import {
    ORDER_FILTERS,
    orderApi,
    orderKeys,
    type NewOrder,
    type Order,
    type OrderQuery,
    type OrderStatusCode,
    type ScheduleInput,
} from '@/entities/order';

export function useOrderList(query: OrderQuery) {
    return useQuery({
        queryKey: orderKeys.list(query),
        queryFn: () => orderApi.list(query),
        placeholderData: keepPreviousData,
    });
}

/** De tellers op de filterpillen: dezelfde zoekterm en dienst, per pil. */
export function useOrderCounts(query: Pick<OrderQuery, 'search' | 'service'>) {
    const keys = ORDER_FILTERS.map((filter) => filter.key);

    return useQuery({
        queryKey: orderKeys.counts(query),
        queryFn: () => orderApi.counts(query, keys),
        placeholderData: keepPreviousData,
    });
}

export function useInspectionTypes() {
    return useQuery({
        queryKey: orderKeys.inspectionTypes(),
        queryFn: () => orderApi.inspectionTypes(),
        staleTime: 10 * 60 * 1_000,
    });
}

/** Ook het opzoeken van een adres gebruikt hem; daarom staat hij in `shared/lib`. */
export { useDebounced } from '@/shared/lib';

/**
 * Na elke schrijfactie: de keuring zelf is bekend (de API geeft hem terug), de
 * lijsten, tellers, planning en het dashboard moeten opnieuw.
 */
export function refreshAfterWrite(client: QueryClient, order: Order): void {
    client.setQueryData(orderKeys.detail(order.uuid), order);
    void client.invalidateQueries({ queryKey: orderKeys.lists() });
    void client.invalidateQueries({ queryKey: ['orders', 'counts'] });
    void client.invalidateQueries({ queryKey: orderKeys.timeline(order.uuid) });
    void client.invalidateQueries({ queryKey: orderKeys.planningAll() });
    void client.invalidateQueries({ queryKey: ['dashboard'] });
}

export function useScheduleOrder() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: { orderUuid: string } & ScheduleInput) => {
            const { orderUuid, ...schedule } = input;

            return orderApi.schedule(orderUuid, schedule);
        },
        onSuccess: (order) => refreshAfterWrite(client, order),
    });
}

/**
 * Een keuring nu starten: toewijzen, plannen op dit moment, en de opname openen.
 *
 * Eén ronde naar de server, en het antwoord is de bijgewerkte keuring plus de opname
 * om heen te gaan. Een 409 betekent dat iemand anders net sneller was of dat de
 * keuring inmiddels iets anders is; dan haalt dit de echte stand op, zodat de knop
 * verandert in wat er nu wél kan.
 */
export function useStartOrder() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: { orderUuid: string; takeOver?: boolean }) =>
            orderApi.start(input.orderUuid, input.takeOver ?? false),
        onSuccess: (started) => refreshAfterWrite(client, started.order),
        onError: (_error, input) => {
            void client.invalidateQueries({ queryKey: orderKeys.detail(input.orderUuid) });
            void client.invalidateQueries({ queryKey: orderKeys.lists() });
        },
    });
}

export function useTransitionOrder() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: { orderUuid: string; status: OrderStatusCode }) =>
            orderApi.transition(input.orderUuid, input.status),
        onSuccess: (order) => refreshAfterWrite(client, order),
        /* Een 409 betekent: iemand anders was sneller. Laat de echte stand zien. */
        onError: (_error, input) => {
            void client.invalidateQueries({ queryKey: orderKeys.detail(input.orderUuid) });
            void client.invalidateQueries({ queryKey: orderKeys.lists() });
        },
    });
}

export function useCancelOrder() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: { orderUuid: string; reason: string }) =>
            orderApi.cancel(input.orderUuid, input.reason),
        onSuccess: (order) => {
            refreshAfterWrite(client, order);
            void client.invalidateQueries({ queryKey: ['credits'] });
        },
    });
}

export function useCreateOrder() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: { order: NewOrder; idempotencyKey: string }) =>
            orderApi.create(input.order, input.idempotencyKey),
        onSuccess: (order) => {
            refreshAfterWrite(client, order);
            void client.invalidateQueries({ queryKey: ['credits'] });
        },
    });
}

export function useInvoiceOrder() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (orderUuid: string) => orderApi.invoice(orderUuid),
        onSuccess: () => {
            void client.invalidateQueries({ queryKey: ['invoices'] });
            void client.invalidateQueries({ queryKey: ['dashboard'] });
        },
    });
}
